import React from 'react';

/**
 * Explains how the Employee Trade Check classifies findings as potential
 * violations ("Hits"), near misses, or needing a timestamp before review.
 */
const EmployeeTradeCheckRulesModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Employee Trade Check rules</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
            <p className="small text-muted">
              From the firm's Code of Ethics: firm personnel will not trade any reportable
              security on the same day as a client's trade unless the transaction occurs after
              the client's trade, is part of a block transaction, or is inconsequential to the
              client's purchase or sale (less than 1% of the security's average daily trading
              volume).
            </p>
            <p className="small">
              Trades are checked for both <strong>same-direction front-running</strong> and{' '}
              <strong>opposite-side conflicts of interest</strong> (e.g. taking the other side of
              client trades).
            </p>

            <h6 className="mt-3">
              <span className="badge bg-danger me-2">Hit</span>
              Potential violation — same day
            </h6>
            <p className="small mb-1">
              A hit is flagged in either of the following situations:
            </p>
            <ol className="small">
              <li className="mb-2">
                <strong>Same direction, employee first:</strong> An employee trade that shares the
                same economic bias as client trades and occurs <strong>before</strong> the matching
                client trade on the same day.
                <ul className="mt-1">
                  <li>Employee buys stock before clients buy the same security.</li>
                  <li>Employee sells stock before clients sell the same security.</li>
                  <li>Employee buys calls before clients buy the same security.</li>
                  <li>Employee sells calls (including covered calls) before clients sell the same security.</li>
                  <li>Employee buys puts before clients sell the same security.</li>
                  <li>Employee sells puts before clients buy the same security.</li>
                </ul>
              </li>
              <li>
                <strong>Opposite side of company trade:</strong> An employee takes the opposite side
                of a client trade on the same day, anytime that day (before or after the company trade).
                <ul className="mt-1">
                  <li>Company sells for clients, and an employee buys for themselves on the same day.</li>
                  <li>Company buys for clients, and an employee sells for themselves on the same day.</li>
                  <li>Applies to equivalent options positions (e.g. employee buying calls when company sells stock, or buying puts when company buys stock).</li>
                </ul>
              </li>
            </ol>

            <h6 className="mt-3">
              <span className="badge bg-warning text-dark me-2">Near miss</span>
              Matching pairing, outside that trading day
            </h6>
            <p className="small">
              A near miss applies when an employee and client trade in the same direction or take
              opposite sides on different trading sessions inside the selected near-miss window
              (up to &plusmn;3 trading days). For example, if the company sells TSLA for clients and
              an employee buys TSLA 1 trading day before or after, it is flagged as a near miss.
              The window counts actual market sessions, so weekends and market holidays don't count
              toward it and don't shrink it — a Friday trade and the following Monday's trade are 1
              trading day apart, not 3 calendar days. Same-day trades in the same direction that
              happen at or after the matching client trade are permitted (provided the employee did not
              also take the opposite side that day) and are not reported.
            </p>

            <h6 className="mt-3">
              <span className="badge bg-secondary me-2">Needs timestamp</span>
              Can't be auto-classified
            </h6>
            <p className="small">
              The employee and matching client trades happened on the same day in the same direction,
              but one of the two execution times is missing or estimated (for example, imported from a
              statement without a timestamp). A reviewer must confirm the actual order of execution
              before it can be marked a hit.
            </p>

            <h6 className="mt-3">Employees who are also clients</h6>
            <p className="small">
              Some employees also have accounts that appear in the firm's client trade
              files. Those self-trades are ignored, so an employee is not flagged for
              trading in their own client account. Matching is fuzzy because employees
              type the account number and broker by hand: punctuation and legal suffixes
              are ignored, known aliases such as "Charles Schwab &amp; Co." vs "Schwab"
              are treated as the same broker, and truncated or masked account numbers
              still match when the last four (or more) digits line up at the same broker.
            </p>

            <h6 className="mt-3">Dividend reinvestment (DRIPs)</h6>
            <p className="small">
              Automatic dividend reinvestment purchases are never flagged as a hit or
              near miss. An employee doesn't choose when a DRIP executes, so it can't be
              front-running — the purchase happens on the dividend's payment date
              regardless of any client activity that day.
            </p>

            <h6 className="mt-3">How trading days are determined</h6>
            <p className="small">
              Market holidays move every year (Good Friday, observed weekend holidays,
              etc.), so the near-miss window looks up the actual NYSE trading calendar
              instead of a hand-maintained holiday list. Each scan checks which calendar
              dates the market was open and skips weekends and holidays when counting the
              window. If that lookup is ever unavailable, the scan falls back to skipping
              weekends only, so a scan is never blocked by a lookup outage.
            </p>

            <h6 className="mt-3">What counts as a "reportable security"?</h6>
            <ul className="small mb-0">
              <li>Option contracts are collapsed to their underlying ticker for comparison.</li>
              <li>Mutual funds and money market funds (five-letter tickers ending in "X") are exempt and never flagged.</li>
            </ul>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeTradeCheckRulesModal;
